import { task } from 'mobx-task'
import { action, computed, observable, runInAction } from 'mobx'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import type { DraftJob } from '@/features/jobs/drafting/DraftJob'
import type {
  BillingMethodListViewModelFactory,
  CardBillingMethodEditorViewModelFactory,
  NewBillingMethodDialogViewModelFactory,
  ListCouponsFn,
  AddCouponFn,
  FetchCouponFn,
  ApplePayViewModelFactory,
} from '@/features/billing'
import type { SnackbarFactory } from '@/features/shell'
import type { BillingMethodListViewModel } from '@/features/billing/profile/BillingMethodList/BillingMethodListViewModel'
import type { CardBillingMethodEditorViewModel } from '@/features/billing/profile/CardBillingMethodEditor/CardBillingMethodEditorViewModel'
import type { NewBillingMethodDialogViewModel } from '@/features/billing/profile/NewBillingMethodDialog/NewBillingMethodDialogViewModel'
import type { ApplePayViewModel } from '@/features/billing/profile/ApplePayViewModel'
import type { Coupon } from '@/features/billing/coupons/Coupon'
import type { DraftJobStore } from '@/features/jobs/drafting/DraftJobStore'
import type { Quote } from '@/features/jobs/drafting/Quote'
import { map, type Observable, Subject } from 'rxjs'

/**
 * Ensures all actions share the same snackbar key.
 */
const ACTION_SNACKBAR_KEY = Symbol('Checkout Billing Snackbar')

/**
 * View-model specifically for billing on checkout.
 */
export class CheckoutBillingViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()
  private readonly _couponChanged$ = new Subject<void>()

  readonly billingMethodListVm: BillingMethodListViewModel
  readonly newBillingMethodDialogVm: NewBillingMethodDialogViewModel
  readonly applePayVm: ApplePayViewModel

  /**
   * If no billing methods exist, we will ask the user to enter card details.
   */
  @observable
  accessor cardBillingMethodEditorVm: CardBillingMethodEditorViewModel | null =
    null

  /**
   * Coupon code being entered, if any.
   */
  @observable accessor newCouponCode = ''

  /**
   * Available coupons for the user.
   */
  @observable accessor availableCoupons: Coupon[] = []

  /**
   * Currently selected coupon to apply.
   */
  @observable accessor selectedCoupon: Coupon | null = null

  /**
   * Emits when there's a change to coupon selection.
   */
  readonly couponChanged$ = this._couponChanged$.asObservable()

  constructor(
    public readonly job: DraftJob,
    private readonly _draftStore: DraftJobStore,
    createBillingMethodListViewModel: BillingMethodListViewModelFactory,
    private readonly _createCardBillingMethodEditorViewModel: CardBillingMethodEditorViewModelFactory,
    createNewBillingMethodDialogViewModel: NewBillingMethodDialogViewModelFactory,
    createApplePayViewModel: ApplePayViewModelFactory,
    quote$: Observable<Quote | null>,
    submit: () => Promise<void>,
    private readonly _listCoupons: ListCouponsFn,
    private readonly _addCoupon: AddCouponFn,
    private readonly _fetchCoupon: FetchCouponFn,
    private readonly _newSnackbar: SnackbarFactory,
  ) {
    this.billingMethodListVm = createBillingMethodListViewModel()
    this.newBillingMethodDialogVm = createNewBillingMethodDialogViewModel()
    this.applePayVm = createApplePayViewModel(
      quote$.pipe(map((q) => q?.price?.upfrontAmount ?? 0)),
      submit,
    )

    this._lifecycle.manage(this.billingMethodListVm)
    this._lifecycle.manage(this.applePayVm)
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Whether billing is enabled for the job, which will prompt
   * the user to enter a payment method.
   */
  @computed get isBillingEnabled() {
    return (
      this.job.legend.isBillingEnabled &&
      this.job.workspace.config.billing.enabled
    )
  }

  /**
   * Whether a coupon action is currently pending.
   */
  @computed get isCouponActionPending() {
    return (
      this.removeCoupon.pending ||
      this.addNewCoupon.pending ||
      this.applyCoupon.pending
    )
  }

  /**
   * Whether coupons are enabled.
   */
  @computed get isCouponsFeatureEnabled() {
    return (
      this.isBillingEnabled && this.job.workspace.config.billing.coupons.enabled
    )
  }

  /**
   * Whether we're currently fetching coupons.
   */
  @computed get isFetchingCoupons() {
    return this.fetchCoupons.pending
  }

  /**
   * Whether we're currently fetching billing information.
   */
  @computed get isFetchingBillingInfo() {
    return this.fetchBillingInfo.pending
  }

  /**
   * Whether any billing actions are currently pending.
   */
  @computed get isBillingActionPending() {
    return this.isCouponActionPending || this.trySaveNewBillingMethod.pending
  }

  /**
   * The currently selected billing method ID, if any.
   */
  @computed get selectedBillingMethodId() {
    return this.billingMethodListVm.profile.defaultBillingMethodId ?? null
  }

  /**
   * Whether the billing state is valid for checkout.
   */
  @computed get isBillingSubmitEnabled() {
    if (!this.isBillingEnabled) {
      return true
    }

    if (this.isFetchingBillingInfo || this.isFetchingCoupons) {
      return false
    }

    if (this.isBillingActionPending) {
      return false
    }

    if (this.cardBillingMethodEditorVm) {
      return this.cardBillingMethodEditorVm.elementsReady
    }

    return Boolean(this.billingMethodListVm.profile.defaultBillingMethodId)
  }

  /**
   * Whether Apple Pay is available
   */
  @computed get isApplePayAvailable() {
    return (
      this.isBillingEnabled &&
      this.applePayVm.canMakePayment &&
      this.applePayVm.paymentRequest
    )
  }

  /**
   * Fetches available coupons for the user.
   */
  fetchCoupons = task.resolved(async (signal?: AbortSignal) => {
    if (!this.isCouponsFeatureEnabled) {
      return
    }

    const [coupons, selectedCoupon] = await Promise.all([
      this._listCoupons(signal),
      this.job.couponId
        ? this._fetchCoupon(this.job.couponId)
        : Promise.resolve(null),
    ])
    runInAction(() => {
      this.availableCoupons = coupons
      this.selectedCoupon = selectedCoupon
    })
  })

  /**
   * Fetches billing information.
   */
  fetchBillingInfo = task(async (signal?: AbortSignal) => {
    if (!this.isBillingEnabled) {
      return
    }

    await Promise.all([
      this.fetchCoupons(signal),
      this.billingMethodListVm.fetchProfile(signal).then(
        action(() => {
          const isMethodListEmpty =
            this.billingMethodListVm.methods.items.length === 0
          if (isMethodListEmpty) {
            this.cardBillingMethodEditorVm =
              this._createCardBillingMethodEditorViewModel()
          }
        }),
      ),
      this.applePayVm.prepare(signal),
    ])
  })

  /**
   * Saves a new billing method if one is being created.
   * Returns true if successful or no new method needed, false if save failed.
   */
  trySaveNewBillingMethod = task.resolved(async () => {
    if (!this.cardBillingMethodEditorVm) {
      return true
    }

    const method = await this.cardBillingMethodEditorVm.save()
    if (!method) {
      return false
    }

    this.clearNewBillingMethodEditor()
    return true
  })

  /**
   * Adds a new coupon.
   */
  addNewCoupon = task.resolved(async () => {
    const code = this.newCouponCode.trim()
    if (!code) {
      return
    }

    const snackbar = this._newSnackbar((s) => s.withKey(ACTION_SNACKBAR_KEY))

    try {
      const newCoupon = await this._addCoupon(code)
      await this._tryApplyCoupon(newCoupon)
      runInAction(() => {
        this.availableCoupons.push(newCoupon)
        this.selectedCoupon = newCoupon
        this.newCouponCode = ''
        this._couponChanged$.next()
      })

      snackbar.update((s) =>
        s
          .type('success')
          .autoDismiss()
          .saying('Success!', 'The promo code has been added.')
          .show(),
      )
    } catch (err: unknown) {
      runInAction(() => {
        snackbar.fromError(err).autoDismiss(10_000)
        this.newCouponCode = ''
      })
    }
  })

  /**
   * Applies a coupon to the job.
   */
  applyCoupon = task.resolved(async (coupon: Coupon) => {
    const snackbar = this._newSnackbar((s) => s.withKey(ACTION_SNACKBAR_KEY))

    try {
      if (!(await this._tryApplyCoupon(coupon))) {
        return
      }
      runInAction(() => {
        this.selectedCoupon = coupon
        this.newCouponCode = ''
        this._couponChanged$.next()
        snackbar
          .saying('Success!', 'Your promo has been applied to this order.')
          .type('success')
          .autoDismiss()
          .show()
      })
    } catch (error: unknown) {
      snackbar.fromError(error).autoDismiss(10_000)
    }
  })

  /**
   * Removes the currently applied coupon, if any.
   */
  removeCoupon = task.resolved(async () => {
    if (!this.selectedCoupon) {
      return
    }

    const snackbar = this._newSnackbar((s) => s.withKey(ACTION_SNACKBAR_KEY))

    try {
      await this._draftStore.removeCoupon(this.job.id)
      runInAction(() => {
        this.selectedCoupon = null
        this._couponChanged$.next()
      })
    } catch (error: unknown) {
      snackbar.fromError(error).autoDismiss(10_000)
    }
  })

  /**
   * Shows the new billing method dialog.
   */
  @action.bound showNewBillingMethodDialog() {
    this.newBillingMethodDialogVm.newMethod()
  }

  /**
   * Clears the new billing method editor.
   */
  @action.bound clearNewBillingMethodEditor() {
    this.cardBillingMethodEditorVm = null
  }

  /**
   * Attempts applying a coupon by first removing the selected one
   * if there is already a coupon applied.
   *
   * @param coupon
   * @private
   */
  private async _tryApplyCoupon(coupon: Coupon) {
    if (this.selectedCoupon) {
      if (this.selectedCoupon.id === coupon.id) {
        return false
      }

      // Remove the selected coupon first.
      await this._draftStore.removeCoupon(this.job.id)
    }

    await this._draftStore.applyCoupon(this.job.id, coupon.id)
    return true
  }
}

export type CheckoutBillingViewModelFactory = (
  quote$: Observable<Quote | null>,
  submit: () => Promise<void>,
) => CheckoutBillingViewModel

/**
 * Factory for creating a {@link CheckoutBillingViewModel}.
 */
export function createCheckoutBillingViewModelFactory(
  job: DraftJob,
  draftStore: DraftJobStore,
  createBillingMethodListViewModel: BillingMethodListViewModelFactory,
  createCardBillingMethodEditorViewModel: CardBillingMethodEditorViewModelFactory,
  createNewBillingMethodDialogViewModel: NewBillingMethodDialogViewModelFactory,
  createApplePayViewModel: ApplePayViewModelFactory,
  listCoupons: ListCouponsFn,
  addCoupon: AddCouponFn,
  fetchCoupon: FetchCouponFn,
  newSnackbar: SnackbarFactory,
): CheckoutBillingViewModelFactory {
  return (quote$: Observable<Quote | null>, submit: () => Promise<void>) =>
    new CheckoutBillingViewModel(
      job,
      draftStore,
      createBillingMethodListViewModel,
      createCardBillingMethodEditorViewModel,
      createNewBillingMethodDialogViewModel,
      createApplePayViewModel,
      quote$,
      submit,
      listCoupons,
      addCoupon,
      fetchCoupon,
      newSnackbar,
    )
}
